import Image from "next/image";
import { AppRoutes } from "@/lib/routeResolver";
import { truncate } from "@/lib/helpers";
import type { GroupsPackage } from "@/lib/groups/packages";
import { formatDuration, PACKAGE_DESCRIPTION_MAX } from "./packages-format";
import PackagePrice from "./PackagePrice";
import PackageCities from "./PackageCities";

interface PackageListCardProps {
  pack: GroupsPackage;
}

/**
 * Horizontal "list" presentation of a groups package: image on the left,
 * details on the right. Links carry `from=groups`. The itinerary shows as city
 * badges (see PackageCities).
 */
export default function PackageListCard({ pack }: PackageListCardProps) {
  const duration = formatDuration(pack.days, pack.nights);
  const summary = truncate(pack.description, PACKAGE_DESCRIPTION_MAX);
  const href = pack.pageURL
    ? AppRoutes.groupsPackage(pack.pageURL)
    : undefined;
  const flyerLabel = `${pack.flyerTitle} of ${pack.packageName}`;

  const img = (
    <Image
      src={pack.photoURL}
      alt={pack.packageName}
      width={480}
      height={320}
      sizes="(max-width: 991px) 100vw, 480px"
      style={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );

  return (
    <div
      className="room-card align-items-start style2 style-flex background-image"
      style={{ backgroundImage: "url(/groups/img/shape/room-shape-alt.png)" }}
    >
      {pack.photoURL ? (
        <div className="box-img global-img">
          {href ? (
            <a
              href={href}
              className="gp-img-link"
              aria-label={pack.packageName}
              title={pack.packageName}
            >
              {img}
            </a>
          ) : (
            img
          )}
          {duration ? <span className="discount">{duration}</span> : null}
        </div>
      ) : null}

      <div className="box-content">
        <h3 className="box-title">
          {href ? (
            <a href={href} title={pack.packageName}>
              {pack.packageName}
            </a>
          ) : (
            <span>{pack.packageName}</span>
          )}
        </h3>

        <PackageCities cities={pack.cities} />

        {summary ? <p className="box-text">{summary}</p> : null}

        <div className="d-flex flex-column align-items-start gap-3">
          <PackagePrice price={pack.startingPrice} />

          <div className="d-flex flex-wrap gap-2">
            {pack.flyerUrl ? (
              <a
                href={pack.flyerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="th-btn2 style2 md"
                title={flyerLabel}
              >
                <i className="fa fa-download me-2"></i>
                <span>{pack.flyerTitle}</span>
                <span className="visually-hidden">{` of ${pack.packageName}`}</span>
              </a>
            ) : null}
            {href ? (
              <a
                href={href}
                className="th-btn2 style2 md"
                title={`View Details of ${pack.packageName}`}
              >
                <i className="fa fa-arrow-right me-2"></i>
                <span>View Details</span>
                <span className="visually-hidden">{` of ${pack.packageName}`}</span>
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
